import {
  DEFAULT_TIMEOUT_SECONDS,
  KIOSK_FLOW_STEPS,
  MAX_BANNER_FILE_NAME_LENGTH,
  MAX_TIMEOUT_SECONDS,
  REQUIRED_FLOW_STEPS,
  type DraftIssue,
  type DraftStep,
  type KioskFlowStep,
  type KioskImageOption,
  type KioskLayoutOption,
} from '../types/kiosk-flow-types';

let draftIdCounter = 0;

export function newDraftId(): string {
  return `step-${++draftIdCounter}`;
}

export function createEmptyDraftStep(): DraftStep {
  return { id: newDraftId(), step: '', layoutName: '', timeoutTarget: '', timeoutSeconds: '' };
}

export function toDraftSteps(screenFlow?: KioskFlowStep[] | null): DraftStep[] {
  return (screenFlow ?? []).map((s) => ({
    id: newDraftId(),
    step: s.step ?? '',
    layoutName: s.layoutName ?? '',
    timeoutTarget: s.timeoutTarget ?? '',
    // Not rounded: 1500 ms shows as "1.5"
    timeoutSeconds: s.timeoutDuration == null || s.timeoutDuration <= 0 ? '' : String(s.timeoutDuration / 1000),
  }));
}

// Stored drafts can hold a name that is no longer a kiosk step
export function isKnownStep(step: string): boolean {
  return (KIOSK_FLOW_STEPS as readonly string[]).includes(step);
}

// Blank means the default; otherwise positive seconds up to the max, with up to 3 decimals (ms precision)
export function isTimeoutValid(timeoutSeconds: string): boolean {
  const value = timeoutSeconds.trim();
  if (value === '') return true;
  const seconds = Number(value);
  return /^\d+(\.\d{1,3})?$/.test(value) && seconds > 0 && seconds <= MAX_TIMEOUT_SECONDS;
}

// Draft rows → the request body. The default 30s is left out.
export function toScreenFlow(draft: DraftStep[]): KioskFlowStep[] {
  return draft.map((d) => {
    const step: KioskFlowStep = { step: d.step, layoutName: d.layoutName };
    if (d.timeoutTarget) step.timeoutTarget = d.timeoutTarget;
    const seconds = d.timeoutSeconds.trim();
    if (seconds !== '' && Number(seconds) !== DEFAULT_TIMEOUT_SECONDS) {
      // Rounded so float error (1.1 * 1000 = 1100.0000000000002) can't reach the server's int
      step.timeoutDuration = Math.round(Number(seconds) * 1000);
    }
    return step;
  });
}

// The kiosk loads the banner from the operator's images folder by name — a path or URL would break it
export function isBannerFileNameValid(fileName: string): boolean {
  const value = fileName.trim();
  return value.length <= MAX_BANNER_FILE_NAME_LENGTH && !/[/\\]/.test(value);
}

export interface DraftAssets {
  // The operator's Asset Manager files; null / undefined while loading or after a failed load,
  // and then nothing is flagged as missing
  layouts?: KioskLayoutOption[] | null;
  images?: KioskImageOption[] | null;
}

// Every reason the draft can't be saved or published — the same rules as the backoffice validator
// (Save: the rows that are there; Publish: also required steps and Asset Manager files), so the
// editor can mark each field before a request is sent
export function validateDraft(draft: DraftStep[], bannerFileName: string, assets: DraftAssets = {}): DraftIssue[] {
  const issues: DraftIssue[] = [];
  const rowIssue = (index: number, field: DraftIssue['field'], message: string, blocks: DraftIssue['blocks'] = 'save') =>
    issues.push({ rowId: draft[index].id, stepIndex: index, field, message, blocks });

  const stepsInFlow = new Set(draft.map((d) => d.step).filter(Boolean));
  const seenSteps = new Set<string>();
  const seenLayouts = new Set<string>();
  const orderWarnings = getOrderWarnings(draft);
  const layoutNames = assets.layouts ? new Set(assets.layouts.map((l) => l.layoutName)) : null;

  draft.forEach((d, index) => {
    if (!d.step) rowIssue(index, 'step', 'Pick a step');
    else if (!isKnownStep(d.step)) rowIssue(index, 'step', `'${d.step}' is not a kiosk step`);
    else if (seenSteps.has(d.step)) rowIssue(index, 'step', `'${d.step}' is already used in this flow`);
    if (d.step) seenSteps.add(d.step);
    if (orderWarnings[d.id]) rowIssue(index, 'step', orderWarnings[d.id]);

    if (!d.layoutName) rowIssue(index, 'layoutName', 'Pick a layout');
    else if (seenLayouts.has(d.layoutName)) rowIssue(index, 'layoutName', `Layout '${d.layoutName}' is already used by another step`);
    else if (layoutNames && !layoutNames.has(d.layoutName)) {
      rowIssue(index, 'layoutName', `Layout '${d.layoutName}' is not in Asset Manager`, 'publish');
    }
    if (d.layoutName) seenLayouts.add(d.layoutName);

    if (d.timeoutTarget && d.timeoutTarget === d.step) rowIssue(index, 'timeoutTarget', "A step can't time out to itself");
    else if (d.timeoutTarget && !stepsInFlow.has(d.timeoutTarget)) {
      rowIssue(index, 'timeoutTarget', `Timeout target '${d.timeoutTarget}' is not a step in this flow`);
    }

    if (!isTimeoutValid(d.timeoutSeconds)) {
      rowIssue(index, 'timeoutSeconds', `Timeout must be above 0 and at most ${MAX_TIMEOUT_SECONDS} seconds, up to 3 decimals`);
    }
  });

  for (const required of REQUIRED_FLOW_STEPS) {
    if (!stepsInFlow.has(required)) {
      issues.push({ field: 'screenFlow', message: `${required} step is required`, blocks: 'publish' });
    }
  }

  const banner = bannerFileName.trim();
  if (banner && !isBannerFileNameValid(banner)) {
    issues.push({
      field: 'promoBannerFileName',
      message:
        banner.length > MAX_BANNER_FILE_NAME_LENGTH
          ? `Promo banner file name must be at most ${MAX_BANNER_FILE_NAME_LENGTH} characters`
          : 'Promo banner must be a file name, not a path or URL',
      blocks: 'save',
    });
  } else if (banner && assets.images && !assets.images.some((image) => image.fileName === banner)) {
    issues.push({ field: 'promoBannerFileName', message: `Promo banner '${banner}' is not in Asset Manager`, blocks: 'publish' });
  }

  return issues;
}

// "Step 2: …" for row issues, the message alone for flow-level ones
export function formatDraftIssue(issue: DraftIssue): string {
  return issue.stepIndex == null ? issue.message : `Step ${issue.stepIndex + 1}: ${issue.message}`;
}

// First reason the draft can't be saved, or null when it's fine.
// Required steps and missing Asset Manager files are checked on Publish.
export function getDraftError(draft: DraftStep[], bannerFileName = ''): string | null {
  const issue = validateDraft(draft, bannerFileName).find((i) => i.blocks === 'save');
  return issue ? formatDraftIssue(issue) : null;
}

// Kiosk screen order rules for the steps in the flow — part of validateDraft, so they block Save.
// Missing required steps are a Publish issue.
export function getOrderWarnings(draft: DraftStep[]): Record<string, string> {
  const warnings: Record<string, string> = {};
  const chargingIndex = draft.findIndex((d) => d.step === 'Charging');
  const paymentIndex = draft.findIndex((d) => d.step === 'Payment');
  draft.forEach((d, index) => {
    if (d.step === 'Loading' && index !== 0) {
      warnings[d.id] = 'Loading must be the first step';
    } else if (d.step === 'Connectors' && index !== 1) {
      warnings[d.id] = 'Connectors must be the second step';
    } else if (d.step === 'AuthAmount' && paymentIndex !== -1 && index > paymentIndex) {
      warnings[d.id] = 'AuthAmount must come before Payment';
    } else if ((d.step === 'StopCharge' || d.step === 'Ending') && chargingIndex !== -1 && index < chargingIndex) {
      warnings[d.id] = `${d.step} must come after Charging`;
    }
  });
  return warnings;
}

// Compares what would be saved, so a row with "30" equals a row left blank
export function isDraftDirty(draft: DraftStep[], screenFlow?: KioskFlowStep[] | null): boolean {
  const saved = toScreenFlow(toDraftSteps(screenFlow));
  return JSON.stringify(toScreenFlow(draft)) !== JSON.stringify(saved);
}
